import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getBroadcasts, getBroadcastDetail, Broadcast } from '@/adminApi';
import { useAdmin } from '@/contexts/AdminContext';

export default function AdminBroadcastsPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Detail modal
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Broadcast | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchBroadcasts = useCallback(() => {
    setLoading(true);
    getBroadcasts(pagination.page, pagination.limit, channelFilter || undefined)
      .then((res) => {
        setBroadcasts(res.data.broadcasts);
        setPagination({
          page: res.data.page,
          totalPages: Math.ceil(res.data.total / res.data.limit),
          total: res.data.total,
          limit: res.data.limit,
        });
      })
      .catch(() => setBroadcasts([]))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.limit, channelFilter]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchBroadcasts();
  }, [isAuthenticated, navigate, fetchBroadcasts]);

  const handleViewDetail = async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    try {
      const res = await getBroadcastDetail(id);
      setDetail(res.data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const channelLabel = (ch: string) => {
    const labels: Record<string, string> = { telegram: 'Telegram', email: 'Email', both: 'Оба' };
    return labels[ch] || ch;
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/admin/dashboard" className="text-xl font-bold text-white hover:text-purple-400 transition">
            ← Назад
          </Link>
          <h1 className="text-xl font-bold text-white">Рассылки ({pagination.total})</h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filter */}
        <div className="mb-6 flex gap-3">
          <select
            value={channelFilter}
            onChange={(e) => { setChannelFilter(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
            className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Все каналы</option>
            <option value="telegram">Telegram</option>
            <option value="email">Email</option>
            <option value="both">Оба</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="px-6 py-12 text-center text-slate-500">Загрузка...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                      <th className="px-6 py-3 font-medium">Дата</th>
                      <th className="px-6 py-3 font-medium">Канал</th>
                      <th className="px-6 py-3 font-medium">Подписчики</th>
                      <th className="px-6 py-3 font-medium">Отправлено</th>
                      <th className="px-6 py-3 font-medium">Ошибки</th>
                      <th className="px-6 py-3 font-medium">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {broadcasts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                          Рассылки не найдены
                        </td>
                      </tr>
                    ) : (
                      broadcasts.map((bc) => (
                        <tr key={bc.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition">
                          <td className="px-6 py-3 text-slate-300 text-sm">
                            {new Date(bc.created_at).toLocaleString('ru-RU')}
                          </td>
                          <td className="px-6 py-3">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-600/30 text-blue-300">
                              {channelLabel(bc.channels)}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-slate-400 text-sm">
                            {bc.subscribers_only ? 'Только подписчики' : 'Все'}
                          </td>
                          <td className="px-6 py-3 text-green-400 text-sm">{bc.sent_count}</td>
                          <td className="px-6 py-3 text-red-400 text-sm">{bc.failed_count}</td>
                          <td className="px-6 py-3">
                            <button
                              onClick={() => handleViewDetail(bc.id)}
                              className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 transition"
                            >
                              Детали
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-sm text-slate-400">
                    Страница {pagination.page} из {pagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition"
                    >
                      ←
                    </button>
                    <button
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition"
                    >
                      →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Detail Modal */}
      {detailId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            {detailLoading ? (
              <div className="text-center text-slate-500 py-8">Загрузка...</div>
            ) : detail ? (
              <>
                <h2 className="text-lg font-bold text-white mb-4">
                  Рассылка от {new Date(detail.created_at).toLocaleString('ru-RU')}
                </h2>
                <dl className="space-y-3 mb-4">
                  <div>
                    <dt className="text-sm text-slate-400">Канал</dt>
                    <dd className="text-white">{channelLabel(detail.channels)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-400">Аудитория</dt>
                    <dd className="text-white">{detail.subscribers_only ? 'Только подписчики' : 'Все верифицированные'}</dd>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <dt className="text-sm text-slate-400">Отправлено</dt>
                      <dd className="text-green-400">{detail.sent_count}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-400">Ошибки</dt>
                      <dd className="text-red-400">{detail.failed_count}</dd>
                    </div>
                  </div>
                </dl>
                <div className="mb-4">
                  <label className="block text-sm text-slate-400 mb-1">Текст рассылки</label>
                  <div className="px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {detail.text}
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => { setDetailId(null); setDetail(null); }}
                    className="px-6 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                  >
                    Закрыть
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center text-red-400 py-8">Не удалось загрузить детали</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
